/**
 * IDENTITY-AUDIT-NAV-001A · Operations "Run identity audit" surface.
 *
 * Route: `/operations/administration/identity-audit`
 *
 * Navigation contract (this tranche):
 *   • Renders inside the shared `<OperationsShell>` so the sidebar,
 *     top bar, notifications bell and Operations navigation destinations
 *     stay reachable — matches Field Team, Blackouts, Administration.
 *   • Explicit "Back to Administration" control at the top of the page
 *     always lands on `/operations/administration` regardless of
 *     history — reuses the unified `<BackButton to="…" />` component
 *     (DEV-003E) so the visual + a11y contract is identical.
 *   • Direct-link and refresh both keep the shell + Back control
 *     because both are declared at the page root — no history-only
 *     fallback path.
 *   • Browser Back/Forward continues to work because the BackButton
 *     uses `navigate(to)` for the deterministic parent path only.
 *
 * Behaviour retained from FIELD-IDENTITY-001A:
 *   • Read-only. Calls `POST /api/operations/identity/reconciliation/dry-run`
 *     only when the user clicks "Run identity audit" — never on mount.
 *   • Renders engineer + customer manifests when a result is present.
 *   • Copy / Download JSON stay disabled until an audit result exists.
 *   • Zero destructive actions anywhere in the tree.
 *   • Auth + Operations permission gates remain enforced by the parent
 *     `<ProtectedRoute role="operations">` in App.js and by the backend
 *     endpoint (which requires `manage_users` + `manage_engineers`).
 */
import React, { useState } from "react";
import { ShieldCheck } from "lucide-react";
import OperationsShell from "@/components/operations/OperationsShell";
import BackButton from "@/components/BackButton";
import { api } from "@/lib/api";

export default function OperationsIdentityAudit() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [audit, setAudit] = useState(null);

    async function runAudit() {
        setError(null); setLoading(true);
        try {
            const data = await api.identityAuditDryRun();
            if (!data || typeof data !== "object" || !data.engineers || !data.customers) {
                throw new Error("Malformed audit response");
            }
            setAudit(data);
        } catch (e) {
            const status = e?.response?.status;
            if (status === 401) setError("Signed out. Please sign in again.");
            else if (status === 403) setError("You need both manage_users and manage_engineers.");
            else setError(e?.message || "Audit failed.");
            setAudit(null);
        } finally { setLoading(false); }
    }

    async function copyJson() {
        if (!audit) return;
        try { await navigator.clipboard.writeText(JSON.stringify(audit, null, 2)); }
        catch { /* no-op */ }
    }

    function downloadJson() {
        if (!audit) return;
        const blob = new Blob([JSON.stringify(audit, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `identity-audit-${new Date().toISOString().slice(0,19)}.json`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
    }

    return (
        <OperationsShell>
            <div
                className="mx-auto max-w-6xl px-4 md:px-8 pt-6 md:pt-10 pb-16"
                data-testid="ops-identity-audit-page"
            >
                <BackButton
                    to="/operations/administration"
                    label="Back to Administration"
                    testid="ops-identity-audit-back"
                    className="mb-4"
                />

                <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#E1E6F0] text-[#131B2F] flex items-center justify-center shrink-0">
                        <ShieldCheck size={22} strokeWidth={1.75} />
                    </div>
                    <div>
                        <div
                            className="text-[10px] uppercase tracking-[0.14em] text-[#4B5565] font-medium"
                            data-testid="ops-identity-audit-breadcrumb"
                        >
                            Operations · Administration
                        </div>
                        <h1 className="text-2xl md:text-3xl text-[#131B2F] font-semibold">
                            Identity audit
                        </h1>
                    </div>
                </div>

                <div
                    className="mt-6 rounded-xl border border-[#E5E7EB] bg-white p-5"
                    data-testid="ops-identity-audit-intro"
                >
                    <p className="text-sm text-[#131B2F] leading-relaxed">
                        This audit reviews the linkages between three areas of the
                        system and flags identities that appear to be duplicated or
                        incorrectly linked:
                    </p>
                    <ul
                        className="mt-3 space-y-1.5 text-sm text-[#131B2F] list-disc pl-5"
                        data-testid="ops-identity-audit-intro-areas"
                    >
                        <li>Users &amp; Security accounts</li>
                        <li>Customer records</li>
                        <li>Field Team resources</li>
                    </ul>
                    <p className="mt-3 text-sm text-[#4B5565] leading-relaxed">
                        Running the audit is <strong>read-only</strong>. It does not
                        merge, delete, archive or modify any record. Results identify
                        issues that require review and are actioned separately using
                        the standard administration surfaces.
                    </p>
                </div>

                <div className="mt-6 flex items-center gap-3 flex-wrap">
                    <button
                        type="button"
                        data-testid="ops-identity-audit-run"
                        onClick={runAudit}
                        disabled={loading}
                        className="px-4 py-2 rounded-md bg-[#131B2F] text-white text-sm disabled:opacity-60"
                    >{loading ? "Running…" : "Run identity audit"}</button>
                    <button
                        type="button"
                        data-testid="ops-identity-audit-copy"
                        disabled={!audit}
                        onClick={copyJson}
                        className="px-3 py-2 rounded-md border border-[#D1D5DB] text-sm disabled:opacity-40"
                    >Copy JSON</button>
                    <button
                        type="button"
                        data-testid="ops-identity-audit-download"
                        disabled={!audit}
                        onClick={downloadJson}
                        className="px-3 py-2 rounded-md border border-[#D1D5DB] text-sm disabled:opacity-40"
                    >Download JSON</button>
                    <span
                        data-testid="ops-identity-audit-readonly-badge"
                        className="ml-auto text-xs px-2 py-1 rounded-full bg-[#EEF2FF] text-[#1E3A8A]"
                    >
                        Read-only — no changes made
                    </span>
                </div>

                {error && (
                    <div
                        data-testid="ops-identity-audit-error"
                        className="mt-4 p-3 rounded-md bg-[#FDF2F2] text-[#7A1E1E] text-sm"
                    >{error}</div>
                )}

                {audit && (
                    <div className="mt-6">
                        <ManifestSection title="Engineers" manifest={audit.engineers} testid="engineers" />
                        <ManifestSection title="Customers" manifest={audit.customers} testid="customers" />
                    </div>
                )}
            </div>
        </OperationsShell>
    );
}

function ManifestSection({ title, manifest, testid }) {
    return (
        <section data-testid={`ops-identity-audit-${testid}`} className="mb-6">
            <h2 className="text-lg font-semibold mb-2">{title}</h2>
            <div className="text-xs text-[#4B5565] mb-2">
                content_hash <code data-testid={`ops-identity-audit-${testid}-hash`}>{manifest.content_hash}</code>
                {" · "} {manifest.summary?.total_groups ?? 0} groups
            </div>
            <div className="space-y-3">
                {(manifest.groups || []).map((g, i) => (
                    <div key={g.signature || i}
                        data-testid={`ops-identity-audit-${testid}-group-${g.signature}`}
                        className={`p-3 rounded-md border ${
                            g.status === "SAFE"       ? "border-[#B7E1B0] bg-[#F5FBEF]" :
                            g.status === "AMBIGUOUS"  ? "border-[#F5D680] bg-[#FEF9E7]" :
                            g.status === "BLOCKED"    ? "border-[#F1B0B0] bg-[#FDF2F2]" :
                                                        "border-[#D1D5DB] bg-white"
                        }`}>
                        <div className="flex items-center gap-2 text-sm">
                            <span className="font-semibold">{g.status}</span>
                            <span className="text-[#4B5565]">{g.signature}</span>
                        </div>
                        {g.canonical_id && (
                            <div className="text-xs mt-1">canonical: <code>{g.canonical_id}</code>
                                {g.canonical_reason && <span className="text-[#4B5565]"> · {g.canonical_reason}</span>}</div>
                        )}
                        {(g.blockers || []).length > 0 && (
                            <ul className="text-xs mt-1 list-disc pl-5 text-[#7A1E1E]">
                                {g.blockers.map((b, j) => <li key={j}>{b}</li>)}
                            </ul>
                        )}
                        {(g.duplicate_ids || []).length > 0 && (
                            <div className="text-xs mt-1">duplicates: {g.duplicate_ids.join(", ")}</div>
                        )}
                        {((testid === "engineers" ? g.engineers : g.users) || []).map((row) => (
                            <div key={row.id} className="text-xs mt-2 border-t pt-2 first:border-0 first:pt-0">
                                <div><code>{row.id}</code> · <b>{row.displayName || row.name || "(no name)"}</b>
                                    {row.email && <span className="text-[#4B5565]"> · {row.email}</span>}
                                    {row.linkedUserId && <span className="text-[#4B5565]"> · linked → <code>{row.linkedUserId}</code></span>}
                                </div>
                                {typeof row.references?._total === "number" && (
                                    <div className="text-[11px] text-[#4B5565]">references total: {row.references._total}</div>
                                )}
                            </div>
                        ))}
                        {Object.keys(g.proposed_rewrites || {}).length > 0 && (
                            <details className="mt-2 text-xs">
                                <summary className="cursor-pointer text-[#4B5565]">Proposed rewrites (read-only preview)</summary>
                                <pre className="mt-1 p-2 bg-white rounded overflow-auto max-h-60"
                                    data-testid={`ops-identity-audit-${testid}-rewrites-${g.signature}`}>
{JSON.stringify(g.proposed_rewrites, null, 2)}
                                </pre>
                            </details>
                        )}
                    </div>
                ))}
            </div>
        </section>
    );
}
